import * as rd from '@devexperts/remote-data-ts'
import {
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from '@reduxjs/toolkit'
import { MapInitParam } from '@spiaggeit/map-canvas'
import { Color, isSpotSubType, SpotType } from '@spiaggeit/spit-core'
import { t } from 'i18next'

import { DecorationCategory } from '@/models/decorationCategories'
import { Service } from '@/models/service'

import { resetAllState } from '@/store/commonActions.ts'
import { Errors } from '@/utils/errors'
import { getMapElementKey } from '@/utils/getMapElementKey'
import { getMapPadding } from '@/utils/map/getMapPadding'
import { getPendingCartToken } from '@/utils/pendingCartToken'
import { httpClient } from '../lib/http/HttpClient'
import {
  MapElementWithSectorId,
  BoundingBox2d,
  MapResponse,
  MapSetupItem,
  MapSetupItemKeyName,
  Sector,
  SectorDetailType,
  WarehousesResponse,
  WarehouseSetup,
} from '../models/map'
import {
  mapSetupElements,
  spotTypesWithoutSetupSeats,
} from '../utils/constants'
import {
  convertPeriodToUnixInteger,
  fromISOtoTimestamp,
} from '../utils/dateUtils'
import { getFormattedMapElement } from '../utils/getFormattedMapElement'
import { getHalfDayValueFromBookingPeriod } from '../utils/halfDay'

import { appSlice } from './appSlice'
import { RootState } from './configureStore'
import { createAppSlice } from './createAppSlice'
import {
  bookingPeriodSelector,
  insertPeriodSlice,
  periodAsUnixTimeSelector,
} from './insertPeriodSlice'
import { licenseSlice } from './licenseSlice'
import { getDecorationCategoryMap } from './mapSlice/utils/getDecorationCategoryMap'
import { sectorRulesSlice } from './sectorRulesSlice'
import { sectorRulesPayloadSelector } from './sectorRulesSlice/selectors/sectorRulesPayloadSelector'
import { servicesSlice } from './servicesSlice'

type MapInitOpts = MapInitParam['opts']

type WarehouseSetupBySectorId = Record<number, WarehouseSetup>

interface ExtendedMapInitOpts extends MapInitOpts {
  elements: MapElementWithSectorId[]
  bestQuadrant: BoundingBox2d | null
}

export type MapSliceState = {
  decorationCategories: rd.RemoteData<string, DecorationCategory[]>
  mapLoading: {
    message?: string
    timestamp: number
  } | null
  mapData: rd.RemoteData<string, Partial<ExtendedMapInitOpts>>
  selectedElement: MapElementWithSectorId | null
  currentSector: Sector | null
  currentSectorDetails: Sector['details'] | null
  sectors: Sector[]
  selectedElementSetupData: {
    selectedElementSetupItems: MapSetupItem[]
    selectedElementSetupLimit: MapSetupItem | null
  }
  warehouse: rd.RemoteData<string, WarehouseSetupBySectorId>
}

const initialState: MapSliceState = {
  currentSector: null,
  currentSectorDetails: null,
  decorationCategories: rd.initial,
  mapData: rd.initial,
  mapLoading: null,
  sectors: [],
  selectedElement: null,
  selectedElementSetupData: {
    selectedElementSetupItems: [],
    selectedElementSetupLimit: null,
  },
  warehouse: rd.initial,
}

export const loadMapData = createAsyncThunk<
  Partial<ExtendedMapInitOpts>,
  { screenWidth: number; screenHeight: number; cellSize: number },
  { rejectValue: string; state: RootState }
>(
  'map/loadMapData',
  async ({ screenWidth, screenHeight, cellSize }, thunkApi) => {
    const state = thunkApi.getState()
    const period = insertPeriodSlice.selectors.period(state)
    const bookingPeriod = bookingPeriodSelector(state)
    const country = appSlice.selectors.country(state)
    const sct = appSlice.selectors.sct(state)
    const channel = appSlice.selectors.channel(state)
    const sectorRuleFields = sectorRulesPayloadSelector(
      state,
      convertPeriodToUnixInteger(period)
    )

    const decorationCategories = mapSlice.selectors.decorationCategories(state)

    if (!period || !period.end || !period.start) {
      throw thunkApi.rejectWithValue(Errors.PeriodNotFound)
    }

    const from = fromISOtoTimestamp(period.start)
    const to = fromISOtoTimestamp(period.end)
    const halfDay = getHalfDayValueFromBookingPeriod(bookingPeriod)
    const license = licenseSlice.selectors.licenseCode(state)
    const preemptionCode = sectorRulesSlice.selectors.preemptionCode(state)
    const unavailableSectors =
      sectorRulesSlice.selectors.unavailableSectors(state)

    try {
      const searchParams = new URLSearchParams()
      if (typeof from === 'number') {
        searchParams.set('from', String(from))
      }
      if (typeof to === 'number') {
        searchParams.set('to', String(to))
      }
      if (Array.isArray(sct) && sct.length > 0) {
        searchParams.set('sct', sct.join(','))
      }
      searchParams.set('halfDay', String(halfDay))
      searchParams.set('v2Payload', '1')
      searchParams.set('screenWidth', String(screenWidth))
      searchParams.set('screenHeight', String(screenHeight))
      searchParams.set('cellWidth', String(cellSize))
      searchParams.set('cellHeight', String(cellSize))
      searchParams.set('channel', channel)
      const pendingCartToken = getPendingCartToken(license)
      if (pendingCartToken !== null) {
        searchParams.set('pendingCartToken', pendingCartToken)
      }
      if (preemptionCode) {
        searchParams.set('preemptionCode', preemptionCode)
      }

      if (sectorRuleFields !== null) {
        Object.entries(sectorRuleFields).forEach((entry) => {
          searchParams.set(`sectorRuleFields[${entry[0]}]`, entry[1])
        })
      }

      const response = await httpClient.fetch<MapResponse>(
        `beaches/${license}/map-v2?${searchParams.toString()}`,
        country
      )

      if (response.status === 'success' && response.data) {
        const data = response.data.result

        const mapPadding = getMapPadding(data)

        const mapData: Partial<ExtendedMapInitOpts> = {
          backgroundUrl: data.backgroundUrl,
          bestQuadrant: data.bestQuadrant,
          elements: data.elements.map((element) =>
            getFormattedMapElement(
              element,
              rd.isSuccess(unavailableSectors) ? unavailableSectors.value : [],
              getDecorationCategoryMap(
                rd.isSuccess(decorationCategories)
                  ? decorationCategories.value
                  : []
              )
            )
          ),
          size: {
            height: data.height + mapPadding,
            width: data.width + mapPadding,
          },
        }
        return mapData
      }

      throw new Error('Unexpected response structure')
    } catch (_error) {
      return thunkApi.rejectWithValue('Failed to fetch map data')
    }
  }
)

export const loadSectors = createAsyncThunk<
  Sector[],
  void,
  { rejectValue: string; state: RootState }
>('map/loadSectors', async (_, thunkApi) => {
  const state = thunkApi.getState()
  const country = appSlice.selectors.country(state)
  const sct = appSlice.selectors.sct(state)
  const license = licenseSlice.selectors.licenseCode(state)

  try {
    const response = await httpClient.fetch<{ result: { sectors: Sector[] } }>(
      `beaches/${license}/sectors`,
      country
    )

    if (response.status === 'success' && response.data) {
      const { sectors } = response.data.result

      if (Array.isArray(sct) && sct.length > 0) {
        return sectors.filter((sector) => sct.includes(sector.header.oldId))
      }

      return sectors
    }

    throw new Error('Unexpected response structure')
  } catch (_error) {
    return thunkApi.rejectWithValue('Failed to fetch sectors data')
  }
})

export const loadDecorationCategories = createAsyncThunk<
  DecorationCategory[],
  void,
  { rejectValue: string; state: RootState }
>('map/loadDecorationCategories', async (_, thunkApi) => {
  try {
    const state = thunkApi.getState()
    const country = appSlice.selectors.country(state)
    const license = licenseSlice.selectors.licenseCode(state)

    const response = await httpClient.fetch<{ result: DecorationCategory[] }>(
      `beaches/${license}/decorations`,
      country
    )

    if (response.status === 'success' && response.data) {
      return response.data.result
    }

    return []
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch decoration categories')
  }
})

export const mapSlice = createAppSlice({
  extraReducers: (builder) => {
    builder.addCase(resetAllState, () => initialState)
    builder
      .addCase(loadMapData.pending, (state) => {
        state.mapData = rd.pending
      })
      .addCase(
        loadMapData.fulfilled,
        (state, action: PayloadAction<Partial<MapInitParam['opts']>>) => {
          state.mapData = rd.success(action.payload)
        }
      )
      .addCase(loadMapData.rejected, (state, action) => {
        state.mapData = rd.failure(action.payload as string)
      })
      .addCase(loadSectors.pending, (state) => {
        state.sectors = []
      })
      .addCase(
        loadSectors.fulfilled,
        (state, action: PayloadAction<Sector[]>) => {
          state.sectors = action.payload
        }
      )
      .addCase(loadSectors.rejected, (state) => {
        state.sectors = []
      })
      .addCase(loadDecorationCategories.fulfilled, (state, action) => {
        state.decorationCategories = rd.success(action.payload)
      })
      .addCase(loadDecorationCategories.rejected, (state, action) => {
        if (typeof action.payload === 'string') {
          state.decorationCategories = rd.failure(action.payload)
        } else {
          state.decorationCategories = rd.failure('Generic error')
        }
      })
      .addCase(loadDecorationCategories.pending, (state) => {
        state.decorationCategories = rd.pending
      })
  },
  initialState,
  name: 'map',
  reducers: (create) => ({
    loadWarehouse: create.asyncThunk<
      WarehouseSetupBySectorId,
      void,
      { rejectValue: string }
    >(
      async (_, thunkApi): Promise<WarehouseSetupBySectorId> => {
        const rootState = thunkApi.getState() as RootState

        const bookingPeriod = bookingPeriodSelector(rootState)
        const country = appSlice.selectors.country(rootState)
        const license = licenseSlice.selectors.licenseCode(rootState)
        const period = periodAsUnixTimeSelector(rootState)

        if (!period) {
          throw thunkApi.rejectWithValue(
            'cannot load warehouse data without a period'
          )
        }

        const searchParams = new URLSearchParams({
          date: String(period.start),
          endDate: String(period.end),
          halfDay: String(getHalfDayValueFromBookingPeriod(bookingPeriod)),
        })

        const res = await httpClient.fetch<WarehousesResponse>(
          `beaches/${license}/warehouses?${searchParams.toString()}`,
          country
        )

        if (res.status === 'error') {
          throw thunkApi.rejectWithValue(res.error)
        }

        return res.data.result.warehouses.reduce<WarehouseSetupBySectorId>(
          (acc, warehouse) => {
            if (Array.isArray(warehouse.availabilities)) {
              return acc
            }

            Object.entries(warehouse.availabilities).forEach((entry) => {
              const [key, availability] = entry
              const sectorId = Number(key)

              if (acc[sectorId]) {
                acc[sectorId] = {
                  beds: acc[sectorId].beds + availability.beds,
                  chairs: acc[sectorId].chairs + availability.chairs,
                  deckChairs:
                    acc[sectorId].deckChairs + availability.deckChairs,
                  maxiBeds: acc[sectorId].maxiBeds + availability.maxiBeds,
                }
              } else {
                acc[sectorId] = {
                  ...availability,
                }
              }
            })

            return acc
          },
          {}
        )
      },
      {
        fulfilled: (state, action) => {
          state.warehouse = rd.success(action.payload)
        },
        options: {
          condition: (_, thunkApi) => {
            const state = thunkApi.getState() as RootState
            const warehouse = mapSlice.selectors.warehouse(state)

            if (rd.isPending(warehouse)) {
              return false
            }

            return true
          },
        },
        pending: (state) => {
          state.warehouse = rd.pending
        },
        rejected: (state, action) => {
          if (typeof action.payload === 'string') {
            state.warehouse = rd.failure(action.payload)
          } else {
            state.warehouse = rd.failure('Generic error')
          }
        },
      }
    ),
    reset: create.reducer(() => initialState),
    selectMapElement: create.reducer(
      (state, action: PayloadAction<MapElementWithSectorId | null>) => {
        state.selectedElement = action.payload
      }
    ),
    setIsMapLoading: create.reducer(
      (state, action: PayloadAction<MapSliceState['mapLoading']>) => {
        state.mapLoading = action.payload
      }
    ),
    updateMapElements: create.reducer(
      (state, action: PayloadAction<MapElementWithSectorId[]>) => {
        if (rd.isSuccess(state.mapData)) {
          state.mapData = rd.success({
            ...state.mapData.value,
            elements: action.payload,
          })
        }
      }
    ),
  }),
  selectors: {
    decorationCategories: (state) => state.decorationCategories,
    mapLoading: (state) => state.mapLoading,
    warehouse: (state) => state.warehouse,
  },
})

export const mapSelectors = {
  error: (state: RootState): string | null =>
    rd.fold<string, Partial<MapInitParam['opts']>, string | null>(
      () => null,
      () => null,
      (e) => e,
      () => null
    )(state.map.mapData),
  hasError: (state: RootState) => rd.isFailure(state.map.mapData),
  isLoading: (state: RootState) => rd.isPending(state.map.mapData),
  mapData: (state: RootState) => {
    if (rd.isFailure(state.map.mapData)) {
      return null
    } else if (rd.isSuccess(state.map.mapData)) {
      return state.map.mapData.value
    }
  },
  mapRemoteData: (state: RootState) => state.map.mapData,
  sectors: (state: RootState) => state.map.sectors,
  selectedElement: (state: RootState) => state.map.selectedElement,
}

export const mapElementsSelector = createSelector(
  [mapSelectors.mapRemoteData],
  (data) =>
    rd.map<
      Partial<ExtendedMapInitOpts>,
      Record<string, MapElementWithSectorId>
    >(
      (a) =>
        a.elements?.reduce(
          (acc, item) => {
            acc[getMapElementKey(item.subType, item.name)] = item
            return acc
          },
          {} as Record<string, MapElementWithSectorId>
        ) ?? {}
    )(data)
)

export const mapElementSelector = createSelector(
  [
    mapElementsSelector,
    (_: RootState, args: { spotName: string; spotType: string }) => args,
  ],
  (mapElements, args) => {
    if (!rd.isSuccess(mapElements)) {
      return undefined
    }
    return mapElements.value[getMapElementKey(args.spotType, args.spotName)]
  }
)

export const mappedSectorsSelector = createSelector(
  [mapSelectors.mapRemoteData, mapSelectors.sectors],
  (mapData, sectors) => {
    if (!rd.isSuccess(mapData) || sectors.length === 0) {
      return []
    }

    const mapElements = mapData.value.elements
    if (!mapElements) {
      return []
    }

    return sectors.map((sector) => {
      // This filters all unavailable map element + sorts them by ids and category
      // (e.g.: Sector 1 may have 5 umbrellas and 2 boats, which we need to store in two separate arrays)
      const filteredElements = mapElements.filter((element, index, self) => {
        const isAvailable = element.style?.iconColor === Color.GREEN
        const isValidSpot = isAvailable && isSpotSubType(element.subType)
        return (
          element.sectorId === sector.header.id &&
          isValidSpot &&
          self.findIndex(
            (e) => e.name === element.name && e.subType === element.subType
          ) === index
        )
      })

      const sectorElementsGroupedByType = filteredElements.reduce<
        Record<string, MapElementWithSectorId[]>
      >((acc, item) => {
        const key = item.subType as SpotType
        acc[key] = acc[key] ?? []
        acc[key].push(item)
        return acc
      }, {})

      return {
        elementsGroupedByType: Object.values(sectorElementsGroupedByType),
        id: sector.header.id,
        image: sector.header.image,
        name: sector.header.name,
      }
    })
  }
)

export const mapCurrentSectorSelector = createSelector(
  [mapSelectors.selectedElement, mapSelectors.sectors],
  (mapSelectedElement, mapSectors) => {
    return mapSectors?.find(
      (sector) => sector.header.id === mapSelectedElement?.sectorId
    )
  }
)

export const mapSelectedElementServices = createSelector(
  [mapCurrentSectorSelector, servicesSlice.selectors.data],
  (currentSector, serviceData) => {
    const filterServicesBySectorId = (services?: Service[]) => {
      if (!currentSector?.header.id || !services) return
      return (
        services.filter(
          (service) =>
            service.sectors.includes(currentSector?.header.id) &&
            !service.featured
        ) || []
      )
    }
    return {
      extraServices: filterServicesBySectorId(serviceData?.extraServices) || [],
      includedServices:
        filterServicesBySectorId(serviceData?.includedServices) || [],
      ticketServices:
        filterServicesBySectorId(serviceData?.ticketServices) || [],
    }
  }
)

export const mapSelectedElementHasVisibleSeatsSelector = createSelector(
  [mapSelectors.selectedElement],
  (mapSelectedElement) => {
    if (!mapSelectedElement || spotTypesWithoutSetupSeats.length === 0) {
      return false
    }
    return !spotTypesWithoutSetupSeats.includes(
      mapSelectedElement.subType as SpotType
    )
  }
)

export const mapCurrentSectorDetailsSelector = createSelector(
  [mapCurrentSectorSelector],
  (mapCurrentSector) => {
    if (!mapCurrentSector) return []
    return mapCurrentSector.details.filter(
      (item) => item.type === SectorDetailType.ONLINE
    )
  }
)

export const mapSelectedElementSetupDataSelector = createSelector(
  [mapCurrentSectorDetailsSelector],
  (sectorDetails) => {
    if (sectorDetails.length === 0) return
    const groupedData = sectorDetails.reduce<Record<string, MapSetupItem>>(
      (acc, item) => {
        acc[item.keyName] = {
          ...acc[item.keyName],
          icon: mapSetupElements[item.keyName as keyof object]['icon'],
          keyName: item.keyName as MapSetupItemKeyName,
          label: t(mapSetupElements[item.keyName as keyof object]['label'], {
            count: 1,
          }),
          [item['keyType']]: item.keyValue ?? 0,
          sectorId: item.sectorId,
        }

        return acc
      },
      {}
    )
    const selectedElementSetupItems = Object.values(groupedData).filter(
      (item: MapSetupItem) =>
        item?.keyName !== MapSetupItemKeyName.COMBINED_LIMIT
    )

    const selectedElementSetupLimit =
      groupedData[MapSetupItemKeyName.COMBINED_LIMIT]
    return { selectedElementSetupItems, selectedElementSetupLimit }
  }
)
